import React, { useEffect, useState } from 'react';
import { useAppStore } from '@/store';
import { TelegramUtils } from '@/utils/telegram';
import { api } from '@/api';
import logoLight from '@/assets/itinder.svg';
import logoDark from '@/assets/itinder_dark.svg';

const GenderSelectScreen: React.FC = () => {
  const { setActiveScreen, updateOnboardingData, setUser, setAuthenticated, setOnboardingStep } = useAppStore();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    TelegramUtils.configureBackButton(false);
    TelegramUtils.configureMainButton({ visible: false });
  }, []);

  const logo = TelegramUtils.isLightTheme ? logoDark : logoLight;

  const handleGenderSelect = async (gender: 'female' | 'male') => {
    updateOnboardingData({ gender } as any);
    TelegramUtils.haptic.light();
    setLoading(true);

    try {
      const savedToken = localStorage.getItem('auth_token');

      if (savedToken) {
        // Ensure token is set in API client before making request
        api.setToken(savedToken);
        console.log('✅ Token restored before gender update');
        
        // User is already authenticated (via Telegram) - update gender
        await api.updateGender(gender);
        console.log('✅ Gender updated via API:', gender);
        
        // Get updated profile
        const profile = await api.getMyProfile();
        setUser(profile);
        setAuthenticated(true);
        
        console.log('📊 Profile after gender update:', {
          gender: profile.gender,
          status: profile.status,
          is_it: profile.is_it,
        });
        
        // Route based on gender
        if (gender === 'female') {
          // For females, show IT choice screen
          setActiveScreen('it-choice');
        } else {
          // For males, is_it is automatically true, go to onboarding
          setOnboardingStep(0);
          setActiveScreen('onboarding');
        }
      } else if (TelegramUtils.isInTelegram) {
        // First time in Telegram - authenticate via initData
        const initData = TelegramUtils.webApp?.initData;
        if (!initData) {
          throw new Error('No Telegram initData available');
        }
        
        console.log('🔐 Authenticating via Telegram initData...');
        const authData = await (api as any).post('/auth/telegram', {
          telegram_init_data: initData,
        });
        
        console.log('✅ Telegram auth successful');
        
        // Set token in api client and storage
        api.setToken(authData.token);
        localStorage.setItem('auth_token', authData.token);
        localStorage.setItem('token_expires', authData.expires_at);
        setAuthenticated(true);
        
        // Now update gender
        await api.updateGender(gender);
        console.log('✅ Gender updated via API:', gender);
        
        // Get updated profile
        const profile = await api.getMyProfile();
        setUser(profile);
        
        console.log('📊 Profile after gender update:', {
          gender: profile.gender,
          status: profile.status,
          is_it: profile.is_it,
        });
        
        // Route based on gender
        if (gender === 'female') {
          setActiveScreen('it-choice');
        } else {
          setOnboardingStep(0);
          setActiveScreen('onboarding');
        }
      } else if (!TelegramUtils.isInTelegram && import.meta.env.DEV) {
        // Development mode - create test user
        const authData = await (api as any).createTestUser(gender);
        console.log('✅ Test user created:', authData.user);
        
        // Set token in api client
        api.setToken(authData.token);
        setAuthenticated(true);
        
        // Route based on gender
        if (gender === 'female') {
          // For females, show IT choice screen
          setActiveScreen('it-choice');
        } else {
          // For males, go to onboarding
          setOnboardingStep(0);
          setActiveScreen('onboarding');
        }
      } else {
        // No token, not in Telegram, not in dev - something went wrong
        throw new Error('No authentication available');
      }
    } catch (error: any) {
      console.error('Failed to set gender:', error);
      const errorMessage = error?.message || 'Unknown error';
      await TelegramUtils.showAlert(`Ошибка при выборе пола: ${errorMessage}. Проверьте подключение к серверу.`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-tg-bg">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-400">Создаем аккаунт...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-tg-bg p-6 safe-area-y">
      {/* Logo */}
      <div className="mb-8">
        <img 
          src={logo} 
          alt="ITinder" 
          className="w-24 h-auto mb-4"
        />
      </div>

      {/* Main Slogan */}
      <div className="text-center mb-12">
        <h2 className="text-2xl font-bold text-white mb-3 leading-tight">
          ITinder
        </h2>
        <p className="text-lg text-neutral-400 mb-2">
          Проверенные айтишники и те,<br/>кто их ищет 🔥
        </p>
      </div>

      {/* Gender Selection */}
      <div className="w-full max-w-sm space-y-4">
        <p className="text-center text-neutral-500 text-sm mb-6">
          Кто ты?
        </p>

        {/* Female Option */}
        <button
          onClick={() => handleGenderSelect('female')}
          className="w-full group"
        >
          <div className="bg-white/5 border-2 border-white/20 rounded-2xl p-6 hover:bg-white/10 hover:border-white/40 transition-all duration-300 active:scale-95">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
                  <span className="text-3xl">👩</span>
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold text-white">
                    Девушка
                  </h3>
                </div>
              </div>
              <svg className="w-6 h-6 text-white/40 group-hover:text-white/80 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </button>

        {/* Male Option */}
        <button
          onClick={() => handleGenderSelect('male')}
          className="w-full group"
        >
          <div className="bg-white/5 border-2 border-white/20 rounded-2xl p-6 hover:bg-white/10 hover:border-white/40 transition-all duration-300 active:scale-95">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
                  <span className="text-3xl">👨</span>
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold text-white">
                    Парень
                  </h3>
                </div>
              </div>
              <svg className="w-6 h-6 text-white/40 group-hover:text-white/80 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-xs text-neutral-500">
                Доступ только для настоящих айтишников
              </p>
            </div>
          </div>
        </button>
      </div>

    </div>
  );
};

export default GenderSelectScreen;
